import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/FormThankYouPopup.scss';

/**
 * Modal thank-you after successful contact / lead / webinar form submit.
 */
const FormThankYouPopup = ({
  open,
  onClose,
  title,
  message,
  status = 'success' // success | partial
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  const resolvedTitle =
    title ||
    (status === 'partial'
      ? t('form_thank_you.partial_title')
      : t('form_thank_you.title'));
  const resolvedMessage =
    message ||
    (status === 'partial'
      ? t('form_thank_you.partial_message')
      : t('form_thank_you.message'));

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="form-thank-you-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          role="presentation"
        >
          <button
            type="button"
            className="form-thank-you-popup__backdrop"
            aria-label={t('form_thank_you.close')}
            onClick={onClose}
          />

          <motion.div
            className={`form-thank-you-popup__dialog form-thank-you-popup__dialog--${status}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className="form-thank-you-popup__close"
              onClick={onClose}
              aria-label={t('form_thank_you.close')}
            >
              <FaTimes aria-hidden="true" />
            </button>

            <div className="form-thank-you-popup__icon" aria-hidden="true">
              <FaCheck />
            </div>

            <h2 id={titleId}>{resolvedTitle}</h2>
            <p id={descId}>{resolvedMessage}</p>

            <button type="button" className="form-thank-you-popup__cta" onClick={onClose}>
              {t('form_thank_you.close_cta')}
            </button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default FormThankYouPopup;
